import { PATHS } from '@ctcells/core'
import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'

import { dispositionHref } from '../screens/batches/dispositionModel.ts'

import { BatchHaltDialogHost } from './BatchHaltDialogHost.tsx'
import { AlarmPopup } from './AlarmPopup.tsx'
import { BatchStripContainer } from './BatchStripContainer.tsx'
import { ConsoleHeader, type ConsoleHeaderProps } from './ConsoleHeader.tsx'
import { fullViewHostProps } from './fullViewHost.ts'
import type { BatchStripProps } from './batchStripTypes.ts'
import { ExplicitStripPickContext } from './stripPick.ts'
import { SurfaceContext } from './surfaceContext.ts'
import { useAlarmBadges } from './useAlarmBadge.ts'
import { useNavAccount } from './useNavAccount.ts'
import { landingAfterStart, useStripFold } from './useStripFold.ts'

import './shell.css'

export interface ConsoleLayoutProps {
  /**
   * 작업 줄 **덮어쓰기**. 값은 `BatchStripContainer` 가 서버에서 받아 채우고,
   * 여기 넘긴 것만 그 위를 덮는다(시험·스토리 용도).
   * 🔴 실제 화면에서 이 통로로 `allowedCommands` 를 채우지 마라 — 그게 낙관적 활성이다.
   */
  strip?: Partial<BatchStripProps>
  /**
   * 사이드바에 내려줄 것 — 보유 권한 · 배지 · 계정. 서버가 준 값만 넣는다.
   * 여기서 넘긴 값이 **세션에서 뽑은 기본값을 이긴다**(테스트·스토리 용도).
   */
  nav?: ConsoleHeaderProps
}

/**
 * 통합 콘솔 셸 — 사이드바 + 상시 작업 줄 + 화면 본문. 와이어프레임 §3-1.
 *
 * ```
 * ┌──────────┬───────────────────────────────────┐
 * │ 사이드바 │ 상시 작업 줄                      │ auto
 * │ 216/64px │───────────────────────────────────│
 * │          │ 화면 본문                         │ 1fr
 * └──────────┴───────────────────────────────────┘
 * ```
 *
 * 🔴 **상단 안전 배너를 되살리지 마라**(2026-09-18 eric 지시로 삭제). 안전 스트림이 붙기 전에는
 *    상시 표시라 화면을 가렸다. 안전 미확인은 E-STOP 점선 테두리가 말한다.
 *
 * ⚠ **제어 불가를 상시로 말하는 자리는 이제 없다**(2026-09-18 eric 지시로 작업 줄 칩도 삭제).
 *    규약은 살아 있다 — stale 이면 상태변경 버튼이 전부 비활성이고 사유가 붙는다(§6).
 *    다만 사유가 닿는 경로가 **버튼 툴팁과 스크린리더뿐**이고, 리소스 화면은 `OfflineNotice` 가
 *    따로 말하지만 **모니터링·패드에는 그것도 없다.** 터치 장비에는 hover 가 없다 — 팀장 보고 대상.
 *
 * 🔴 **WebSocket 연결의 주인은 이 셸이 아니라 그 위의 `RealtimeProvider` 다**(`mountApp`).
 *    라우터보다 위에 있어서 화면이 바뀌어도 연결이 유지된다. 화면이 연결을 만들지 않는다.
 *
 * 🔴 **사이드바 사용자는 실물 세션에서 온다**(`useNavAccount`). 화면이 아니라 셸이 쥐는 이유는
 *    사용자 영역이 라우트와 무관하게 계속 떠 있기 때문이다 — 화면마다 세션을 읽지 않는다.
 *    `dataSourceFor('auth')` 가 `demo` 면 예전처럼 fixture 계정이 뜬다.
 *
 * 이벤트 로그는 작업 상세와 모니터링에서 제공한다. 상단 작업 줄에는 로그 토글을 두지 않는다.
 */
export function ConsoleLayout({ strip, nav }: ConsoleLayoutProps = {}) {
  const navAccount = useNavAccount()
  /*
   * 🔴 **활성 알람 배지도 셸이 쥔다**(2026-09-22 · `useAlarmBadge.ts`). 헤더는 라우트와
   *    무관하게 계속 떠 있으므로 화면마다 묻지 않는다 — 계정 영역과 같은 결이다.
   * 🔴 **부가 조회다.** 403·끊김이면 배지가 안 뜰 뿐 헤더도 본문도 그대로 산다.
   */
  const badges = useAlarmBadges()
  const navigate = useNavigate()
  /*
   * 🔴 **작업 줄은 어느 메뉴에서든 모니터링의 그 줄 하나다**(eric 2026-10-03 「다른 메뉴에 가도 지금 모니터링에서의 상시창
   *    내용이 보이게 해줘」). 종전(디자인팀 시안 2026-09-30 §2)에는 경로가 2행 모습을 골라 모니터링 밖은 축약형(`compact` ·
   *    현재 공정 한 줄)이었다 — 그 분기(`stripVariantFor`)를 콘솔에서 걷었다. `variant` 를 안 넘기면 `full` 이다.
   *    ⚠ 태블릿(`TabletLayout`)은 종전 경로 분기 그대로다.
   * 🔴 **대신 메뉴가 접힘 기본값을 정한다**(같은 날 「다른 메뉴는 줄인 상태, 모니터링 페이지는 펼친 상태」 · `useStripFold`).
   *    모니터링은 펼침 · 그 밖은 접힘. 단추로 바꾸면 그 메뉴에 있는 동안 따르고, 메뉴를 옮기면 그 메뉴의 기본값으로 선다.
   *    접으면 작업 줄이 한 줄로 줄고 본문 행(`minmax(0, 1fr)`)이 그만큼 위로 올라온다 — 본문이 따로 계산하는 높이는 없다.
   */
  const pathname = useLocation().pathname
  const fold = useStripFold(pathname)
  const stripCollapsed = fold.collapsed
  /*
   * 시작 응답이 오는 «그때»의 경로 — 사전 점검 · 확약 창을 지나는 사이 사람이 메뉴를 옮겼을 수 있어 렌더 때 값이 아니라 마지막 값을 본다.
   */
  const pathnameRef = useRef(pathname)
  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])

  return (
    <SurfaceContext value="console">
      {/* 🔴 표면 선언 — 확약 창구(`confirm_source`)가 여기서 정해진다(`surfaceContext.ts`) */}
      {/*
        🔴 통합 콘솔은 «사람이 고른 작업만» 작업 줄에 띄운다(eric 2026-10-22 · `stripPick.ts`) — 작업 줄 · 모니터링이 같이 읽는다.
           장비 관점 콘솔 · 태블릿은 이 문맥을 열지 않아 종전(첫 건 대체)이다.
      */}
      <ExplicitStripPickContext value>
        {/* `shell--console` — 작업 줄 카드 겉면의 걸쇠(`shell.css`). 스킨 없는 앱(장비 관점)에서는 아무 일도 안 한다 */}
        <div className="shell shell--header shell--console">
          <div className="shell__cols">
            <main className="shell__main">
              {/* 🔴 헤더가 사이드바를 대신한다(2026-09-18). 작업 줄은 그 «아래»에 고정이다 */}
              {/* ⚠ `nav` 가 마지막이다 — 시험·스토리가 넘긴 값이 실물 배지를 이긴다 */}
              <ConsoleHeader badges={badges} {...navAccount} {...nav} />
              {/*
                🔴 **이동은 셸이 준다 — 작업 줄은 경로를 모른다.** 목록 화면이 이미 같은 식으로
                   간다(`consoleBatchViews.tsx`). 경로 문자열을 새로 짓지 말고 `PATHS` 를 쓴다.
              */}
              <BatchStripContainer
                // 🔴 통합 콘솔만 — 2행에 대기 · 완료 영역(eric 2026-10-06)
                flowZones
                // 🔴 통합 콘솔만 — 요약 카드 「예상」 줄(서버 값 미수신 · BR-09 · eric 2026-10-06)
                tallyEstimate
                // 🔴 통합 콘솔만 — E-STOP 아랫줄 「전체 비상 정지」를 뺀다(접근 이름은 그대로 · eric 2026-10-06)
                estopCaption={false}
                nameFirst
                collapsed={stripCollapsed}
                onToggleCollapsed={fold.toggle}
                onOpenBatch={(batchId) => void navigate(`${PATHS.batches}/${batchId}`)}
                /* 시료 처분 대기 안내 → 작업 상세 + 처분 창(`?disposition=open`) */
                onOpenDisposition={(batchId) =>
                  void navigate(dispositionHref(PATHS.batches, batchId))
                }
                onOpenRecipe={(recipeId) => void navigate(`${PATHS.recipes}/${recipeId}`)}
                onSelectBatch={() => void navigate(PATHS.batches)}
                /*
                 * 🔴 [작업 선택]이 목록으로 가지 않고 **그 자리 드롭다운**을 연다(eric 2026-10-22 「작업 선택을 클릭하면.. 작업 메뉴로
                 *    가지 말고.. 작업 아이디 혹은 작업 이름이 뜨게 해줘..」 · `StripBatchPicker`). 통합 콘솔만 켠다 — 고를 작업이 없으면
                 *    메뉴 안의 [작업 추가]가 새 작업 창으로 간다. 켜는 스위치는 아래 `ExplicitStripPickContext` 다.
                 */
                onNewBatch={() => void navigate(PATHS.batchNew)}
                /*
                 * 🔴 **시작이 받아들여지면 모니터링으로 간다**(eric 2026-10-26 「작업을 선택하고 시작을 누르면 모니터링 화면으로 가게 해줘」).
                 *    서버가 시작을 받은 «뒤»(202)에만 부른다 — 응답 전에 옮기지 않는다(낙관적 이동 금지). 사전 점검 막힘 · 409 · 403 은
                 *    여기 안 온다 → 지금 화면에서 사유가 선다. 이미 모니터링 메뉴(하위 탭 포함)면 그대로 둔다(탭을 바꾸지 않는다).
                 *    ⚠ 태블릿 · 장비 관점 셸은 이 콜백을 안 넘겨 종전 그대로다.
                 */
                onStarted={() => {
                  const to = landingAfterStart(pathnameRef.current)
                  if (to !== undefined) void navigate(to)
                }}
                {...(strip === undefined ? {} : { strip })}
              />
              {/* 영상 확대가 덮는 자리 — 머리줄 · 작업 줄(E-STOP)은 밖이다(`fullViewHost.ts`) */}
              <div className="shell__body" {...fullViewHostProps}>
                <Outlet />
                {/*
                  🔴 알람 팝업도 셸이 쥔다(eric 2026-10-28 · `useAlarmPopup.ts`) — 화면을 옮겨도 뜬다. 본문 칸 안에 두어
                     덮개가 머리줄 · 작업 줄(E-STOP)을 덮지 않는다(`scope="body"`). 닫기는 알람을 바꾸지 않는다(DP-02)
                */}
                <AlarmPopup />
                <BatchHaltDialogHost />
              </div>
            </main>
          </div>
        </div>
      </ExplicitStripPickContext>
    </SurfaceContext>
  )
}
